import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  MyLibraryList,
  MyLibraryModal,
  ProductFilterStrainList,
  LibraryProductListingFilter,
  ProductFilterProductTypeList,
  ProductFilterManufacturerList,
  LibraryProductListingQueryParameters,
  ProductFilterStrainListingQueryParameters,
  ProductFilterProductTypeListingQueryParameters,
  ProductFilterManufacturerListingQueryParameters,
} from "./types";

const initialState: MyLibraryModal = {
  myLibrayList: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  libraryProductListingQueryParameters: {
    limit: 10,
    pageNo: 1,
  },

  libraryProductListingFilter: {
    sortBy: 0,
    strainName: [],
    productType: [],
    manufacturerName: [],
    sortCriteria: "createdAt",
  },

  productFilterManufacturerList: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  productFilterManufacturerListingQueryParameters: {
    limit: 10,
    pageNo: 1,
    searchKey: "",
  },

  productFilterStrainList: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  productFilterStrainListingQueryParameters: {
    limit: 10,
    pageNo: 1,
    searchKey: "",
  },

  productFilterProductTypeList: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  productFilterProductTypeListingQueryParameters: {
    limit: 10,
    pageNo: 1,
    searchKey: "",
  },

  hasMore: false,
  hasMoreStrain: false,
  hasMoreManufacturer: false,
  hasMoreProductType: false,
};

const MyLibrarySlice = createSlice({
  name: "myLibrarySlice",
  initialState,
  reducers: {
    setMyLibraryListingData: (
      state: MyLibraryModal,
      action: PayloadAction<MyLibraryList>,
    ) => {
      const newPayload: MyLibraryList = action.payload;

      state.myLibrayList = {
        ...newPayload,
        data: [...state.myLibrayList.data, ...newPayload.data],
      };
      state.hasMore = newPayload.totalPage > newPayload.pageNo;
    },

    setResetMyLibraryListing: (state) => {
      state.myLibrayList = {
        data: [],
        total: 0,
        limit: 10,
        pageNo: 1,
        nextHit: 0,
        totalPage: 0,
      };
      state.hasMore = false;

      state.libraryProductListingQueryParameters = {
        limit: 10,
        pageNo: 1,
      };
    },

    setResetLibraryProductFilter: (state) => {
      state.libraryProductListingFilter = {
        sortBy: 0,
        strainName: [],
        productType: [],
        manufacturerName: [],
        sortCriteria: "createdAt",
      };
    },

    setLibraryProductListingQueryParameters: (
      state: MyLibraryModal,
      action: PayloadAction<LibraryProductListingQueryParameters>,
    ) => {
      state.libraryProductListingQueryParameters = {
        ...action.payload,
      };
    },

    setIncrementPageNo: (state: MyLibraryModal) => {
      state.libraryProductListingQueryParameters.pageNo += 1;
    },

    setHasMore: (state: MyLibraryModal, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    setHasMoreManufacturer: (
      state: MyLibraryModal,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreManufacturer = action.payload;
    },

    setHasMoreStrain: (
      state: MyLibraryModal,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreStrain = action.payload;
    },

    setHasMoreProductType: (
      state: MyLibraryModal,
      action: PayloadAction<boolean>,
    ) => {
      state.hasMoreProductType = action.payload;
    },

    setProductFilterManufacturerListingData: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterManufacturerList>,
    ) => {
      const newPayload: ProductFilterManufacturerList = action.payload;

      state.productFilterManufacturerList = {
        ...newPayload,
        data: [...state.productFilterManufacturerList.data, ...newPayload.data],
      };
      state.hasMoreManufacturer = newPayload.totalPage > newPayload.pageNo;
    },

    setProductFilterManufacturerListingQueryParameters: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterManufacturerListingQueryParameters>,
    ) => {
      state.productFilterManufacturerListingQueryParameters = {
        ...action.payload,
      };
    },

    setResetProductFilterManufacturerListing: (state) => {
      state.productFilterManufacturerList = {
        data: [],
        total: 0,
        limit: 10,
        pageNo: 1,
        nextHit: 0,
        totalPage: 0,
      };
      state.hasMoreManufacturer = false;

      state.productFilterManufacturerListingQueryParameters = {
        limit: 10,
        pageNo: 1,
        searchKey: "",
      };
    },

    setIncrementFilterManufacturerListingPageNo: (state: MyLibraryModal) => {
      state.productFilterManufacturerListingQueryParameters.pageNo += 1;
    },

    setProductFilterStrainListingData: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterStrainList>,
    ) => {
      const newPayload: ProductFilterStrainList = action.payload;

      state.productFilterStrainList = {
        ...newPayload,
        data: [...state.productFilterStrainList.data, ...newPayload.data],
      };
      state.hasMoreStrain = newPayload.totalPage > newPayload.pageNo;
    },

    setResetProductFilterStrainListing: (state) => {
      state.productFilterStrainList = {
        data: [],
        total: 0,
        limit: 10,
        pageNo: 1,
        nextHit: 0,
        totalPage: 0,
      };
      state.hasMoreStrain = false;

      state.productFilterStrainListingQueryParameters = {
        limit: 10,
        pageNo: 1,
        searchKey: "",
      };
    },

    setIncrementFilterStrainListingPageNo: (state: MyLibraryModal) => {
      state.productFilterStrainListingQueryParameters.pageNo += 1;
    },

    setProductFilterStrainListingQueryParameters: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterStrainListingQueryParameters>,
    ) => {
      state.productFilterStrainListingQueryParameters = {
        ...action.payload,
      };
    },

    setProductFilterProductTypeListingData: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterProductTypeList>,
    ) => {
      const newPayload: ProductFilterProductTypeList = action.payload;

      state.productFilterProductTypeList = {
        ...newPayload,
        data: [...state.productFilterProductTypeList.data, ...newPayload.data],
      };
      state.hasMoreProductType = newPayload.totalPage > newPayload.pageNo;
    },

    setResetProductFilterProductTypeListing: (state) => {
      state.productFilterProductTypeList = {
        data: [],
        total: 0,
        limit: 10,
        pageNo: 1,
        nextHit: 0,
        totalPage: 0,
      };
      state.hasMoreProductType = false;

      state.productFilterProductTypeListingQueryParameters = {
        limit: 10,
        pageNo: 1,
        searchKey: "",
      };
    },

    setIncrementFilterProductTypeListingPageNo: (state: MyLibraryModal) => {
      state.productFilterProductTypeListingQueryParameters.pageNo += 1;
    },

    setProductFilterProductTypeListingQueryParameters: (
      state: MyLibraryModal,
      action: PayloadAction<ProductFilterProductTypeListingQueryParameters>,
    ) => {
      state.productFilterProductTypeListingQueryParameters = {
        ...action.payload,
      };
    },

    setSortByCriteria: (
      state: MyLibraryModal,
      action: PayloadAction<
        string | "A-Z" | "Z-A" | "New to Old" | "Old to New"
      >,
    ) => {
      switch (action.payload) {
        case "A-Z":
          state.libraryProductListingFilter = {
            ...state.libraryProductListingFilter,
            sortCriteria: "name",
            sortBy: 1,
          };
          break;
        case "Z-A":
          state.libraryProductListingFilter = {
            ...state.libraryProductListingFilter,
            sortCriteria: "name",
            sortBy: 0,
          };
          break;
        case "New to Old":
          state.libraryProductListingFilter = {
            ...state.libraryProductListingFilter,
            sortCriteria: "createdAt",
            sortBy: 0,
          };
          break;
        case "Old to New":
          state.libraryProductListingFilter = {
            ...state.libraryProductListingFilter,
            sortCriteria: "createdAt",
            sortBy: 1,
          };
          break;
        default:
          break;
      }
    },

    setProductListingFilter: (
      state: MyLibraryModal,
      action: PayloadAction<LibraryProductListingFilter>,
    ) => {
      state.libraryProductListingFilter = {
        ...state.libraryProductListingFilter,
        ...action.payload,
      };
    },
  },
});

export const {
  setHasMore,
  setSortByCriteria,
  setIncrementPageNo,
  setHasMoreManufacturer,
  setMyLibraryListingData,
  setProductListingFilter,
  setResetMyLibraryListing,
  setResetLibraryProductFilter,
  setProductFilterStrainListingData,
  setResetProductFilterStrainListing,
  setIncrementFilterStrainListingPageNo,
  setProductFilterProductTypeListingData,
  setProductFilterManufacturerListingData,
  setLibraryProductListingQueryParameters,
  setResetProductFilterProductTypeListing,
  setResetProductFilterManufacturerListing,
  setIncrementFilterProductTypeListingPageNo,
  setIncrementFilterManufacturerListingPageNo,
  setProductFilterStrainListingQueryParameters,
  setProductFilterProductTypeListingQueryParameters,
  setProductFilterManufacturerListingQueryParameters,
} = MyLibrarySlice.actions;
export default MyLibrarySlice.reducer;
